import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useCallback, useMemo, useState, type SyntheticEvent } from 'react';

import { useGetNewDatasetReleasesQuery } from '@verantos-dev/data-access-datasets';
import { useUpdateProjectDatasetMutation } from '@verantos-dev/data-access-projects';
import type { DropdownListItem } from '@verantos-dev/shared-components';

interface UseSwitchDatasetDialogArgs {
  projectId: string;
  /** The project's current active dataset, pre-selected when the dialog opens. */
  currentDatasetReleaseId?: string | null;
  currentDatasetReleaseName?: string | null;
  onClose: () => void;
  /** Fired with the newly active dataset once the switch succeeds. */
  onSwitched?: (datasetReleaseId: string, datasetReleaseName: string) => void;
}

export interface UseSwitchDatasetDialogResult {
  releases: DropdownListItem[];
  selectedRelease: DropdownListItem | undefined;
  onChangeRelease: (
    event: SyntheticEvent,
    value: DropdownListItem | DropdownListItem[] | null
  ) => void;
  onSubmit: () => void;
  closeDialog: () => void;
  isLoadingReleases: boolean;
  releasesError: FetchBaseQueryError | SerializedError | undefined;
  isSubmitting: boolean;
  submittingError: FetchBaseQueryError | SerializedError | undefined;
  isValidToSubmit: boolean;
  /** Display name of the selection, for the success copy. */
  selectedDatasetName: string;
}

/**
 * Behaviour for the switch-active-dataset dialog: the same dataset options the
 * create-project picker offers, pre-selected to the project's current one, and
 * the PATCH that moves it. Intl-free so the view owns copy.
 *
 * Submitting the dataset already in effect is blocked rather than sent — the
 * server treats it as a no-op, but the success copy would claim a change that
 * did not happen.
 */
export const useSwitchDatasetDialog = ({
  projectId,
  currentDatasetReleaseId,
  currentDatasetReleaseName,
  onClose,
  onSwitched,
}: UseSwitchDatasetDialogArgs): UseSwitchDatasetDialogResult => {
  const {
    data: releasesData,
    isLoading: isLoadingReleases,
    error: releasesError,
  } = useGetNewDatasetReleasesQuery(undefined);

  const [updateProjectDataset, { isLoading: isSubmitting, error: submitError, reset }] =
    useUpdateProjectDatasetMutation();

  const releases: DropdownListItem[] = useMemo(
    () =>
      (releasesData?.data ?? []).map((release) => ({
        value: release.releaseId,
        displayValue: release.releaseName,
      })),
    [releasesData]
  );

  const [selectedRelease, setSelectedRelease] = useState<DropdownListItem | undefined>(() =>
    currentDatasetReleaseId
      ? {
          value: currentDatasetReleaseId,
          displayValue: currentDatasetReleaseName ?? currentDatasetReleaseId,
        }
      : undefined
  );

  const onChangeRelease = useCallback(
    (_: SyntheticEvent, value: DropdownListItem | DropdownListItem[] | null) => {
      if (value && !Array.isArray(value)) {
        setSelectedRelease(value);
      }
    },
    []
  );

  const closeDialog = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const selectedDatasetName = selectedRelease?.displayValue?.toString() ?? '';

  const onSubmit = useCallback(() => {
    if (!selectedRelease?.value) return;
    updateProjectDataset({
      projectId,
      datasetReleaseId: selectedRelease.value,
      datasetReleaseName: selectedDatasetName,
    })
      .unwrap()
      .then(() => {
        onSwitched?.(selectedRelease.value, selectedDatasetName);
      })
      .catch(() => {
        // Surfaced through `submittingError` by the dialog's error state.
      });
  }, [onSwitched, projectId, selectedDatasetName, selectedRelease, updateProjectDataset]);

  return {
    releases,
    selectedRelease,
    onChangeRelease,
    onSubmit,
    closeDialog,
    isLoadingReleases,
    releasesError,
    isSubmitting,
    submittingError: submitError,
    isValidToSubmit:
      selectedRelease !== undefined && selectedRelease.value !== currentDatasetReleaseId,
    selectedDatasetName,
  };
};
